<template>
  <v-card border>
    <b-section-loader :loading="$loading.isLoading('init')">
      <v-card-text>
        <div class="d-flex align-center justify-space-between ga-3">
          <div class="d-flex align-center ga-3">
            <div class="icon--blue">
              <v-icon icon="mdi-file-document-outline" size="23"></v-icon>
            </div>

            <div>
              <div class="font-weight-bold font-size-18">
                {{ documentSource.definition?.name }}
                {{ documentSource.docTypeClass.name }}
              </div>
              <div class="d-flex align-center ga-3">
                <span class="text-disabled">
                  <span v-if="activeVersion" class="text-disabled">
                    Версія {{ activeVersion.version }} |
                  </span>
                  {{ new CustomDate(documentSource.created_at).toString() }}
                </span>
                <v-chip
                  :color="getStatusColor(documentSource.status)"
                  rounded="pill"
                  density="compact">
                  <b>{{
                    keyToLabel(documentSource.status, 'documentStatus')
                  }}</b>
                </v-chip>
              </div>
            </div>
          </div>
          <div class="d-flex align-center ga-2">
            <v-btn
              border
              density="comfortable"
              icon
              variant="flat"
              color="white">
              <v-icon icon="mdi-tray-arrow-down" color="grey-darken-3"></v-icon>
            </v-btn>
            <v-btn
              border
              density="comfortable"
              icon
              variant="flat"
              color="white">
              <v-icon icon="mdi-printer-outline" color="grey-darken-3"></v-icon>
            </v-btn>
            <b-action-menu v-if="actions.length" :actions="actions">
              <template #activator="{ props }">
                <v-btn v-bind="props" density="default">
                  <v-icon icon="mdi-dots-horizontal" class="mr-2"></v-icon>
                  <span>Дії</span>
                </v-btn>
              </template>
            </b-action-menu>
          </div>
        </div>
      </v-card-text>

      <v-divider class="my-3"></v-divider>

      <v-card-text>
        <v-row>
          <v-col cols="9">
            <document-preview></document-preview>
          </v-col>
          <v-col cols="3">
            <b-label class="mb-3" label="Інформація про реєстрацію"></b-label>

            <b-input
              class="mb-3"
              label="Журнал реєстрації"
              :model-value="documentSource.journal?.name || '---'"
              readonly
              hide-details></b-input>
            <b-input
              class="mb-3"
              label="Реєстраційний номер"
              :model-value="documentSource.registrationNumber || '---'"
              readonly
              hide-details></b-input>
            <b-input
              class="mb-3"
              label="Дата реєстрації"
              :model-value="
                new CustomDate(documentSource.registrationDate).toString() ||
                '---'
              "
              readonly
              hide-details></b-input>
            <b-input
              class="mb-3"
              label="Автор"
              :model-value="
                getPersonName(documentSource.creator, { initials: true })
              "
              readonly
              hide-details></b-input>
            <b-input
              class="mb-3"
              label="Підрозділ"
              :model-value="documentSource.executor.department.name"
              readonly
              hide-details></b-input>
          </v-col>
        </v-row>
      </v-card-text>
    </b-section-loader>
  </v-card>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { initDocument } from '@/pages/document/single/document'
import { useDocumentFlow } from '@/pages/document/single/composables/documentFlow'
import { BSectionLoader, BLabel, BActionMenu } from 'best-modules/components'
import { CustomDate, getPersonName } from 'best-modules/utils'
import { useBreadcrumb } from '@/plugins'
import { handleAsync } from 'best-modules/plugins'
import { keyToLabel } from '@/utils/key-to-label'
import { getStatusColor } from '@/pages/document/helpers'
import DocumentPreview from '@/pages/document/single/tabs/Preview.vue'
import { fillDirectory } from '@/directory'

defineOptions({
  name: 'DocumentSingle',
})
const breadcrumb = useBreadcrumb()
const {
  documentSource,
  activeVersion,
  getDocument,
  registerDocument,
  rejectRegistration,
  returnFromRegistration,
  sendToReview,
} = initDocument()

const { allowsSendToReview } = useDocumentFlow(
  () => documentSource.docTypeClass?.flowType
)

const actions = computed(() => {
  if (documentSource.status === 'on_registration') {
    return [
      {
        title: 'Зареєструвати',
        icon: 'mdi-check',
        iconProps: { color: 'success' },
        action: () => registerDocument(),
      },
      {
        title: 'Не реєструвати',
        icon: 'mdi-cancel',
        iconProps: { color: 'grey' },
        action: () => rejectRegistration(),
      },
      {
        title: 'Повернути з реєстрації',
        icon: 'mdi-rotate-left',
        iconProps: { color: 'warning' },
        action: () => returnFromRegistration(),
      },
    ]
  }

  if (documentSource.status === 'under_review' && allowsSendToReview.value) {
    return [
      {
        title: 'Передати на розгляд',
        icon: 'mdi-account-arrow-right-outline',
        iconProps: { color: 'info' },
        action: () => sendToReview(),
      },
    ]
  }

  return []
})

handleAsync('init', async () => {
  await fillDirectory('docTypeFlowTypes')
  await getDocument()

  const breadcrumbPrevItem =
    documentSource.status === 'on_registration'
      ? {
        index: 0,
        title: 'Очікують реєстрації',
        to: { name: 'document-list-on-registration' },
      }
      : {
        index: 0,
        title: 'Зареєстровані',
        to: { name: 'document-list-registered' },
      }
  breadcrumb.set([
    breadcrumbPrevItem,
    {
      index: 1,
      title: `${documentSource.docTypeClass.name} ${documentSource.definition.name}`,
      disabled: true,
    },
  ])
})
</script>
